import { Card, Text } from '@mantine/core'
import LoadingSkeleton from '@v2/commons/components/LoadingSkeletonRows/LoadingSkeletonRows.component'
import showModalNotification from '@v2/commons/modals/ModalNotification/showModalNotification'
import showModalNotificationError from '@v2/commons/modals/ModalNotification/showModalNotificationError'
import { DEFAULT_ROUTES } from '@v2/domains/routing/types/routing.types'
import { getRoutePath } from '@v2/domains/routing/utils/route.utils'
import { useEffect } from 'react'

import withErrorBoundaryAndPolicyCheck from 'src/v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'
import { TBreadCrumbItems } from 'src/v2/commons/components/Breadcrumb'
import BasicTemplate from 'src/v2/commons/templates/BasicTemplate'

import AssociatedInformation from '../components/AssociatedInformation.component'
import RecordInformation from '../components/RecordInformation.component'
import UseMergePersons from '../hooks/useMergeRecords.hook'
import styles from './person-merging-tool.module.scss'

const breadCrumbItems: TBreadCrumbItems = [
  { title: 'Home' },
  { title: 'Persons' },
  { title: 'Merge Duplicates' },
]

const PersonMerginToolView = () => {
  const {
    tableData,
    form: recodrInformationForm,
    setCellValue,
    setEntireColumn,
    isChecked,
    onSubmit,
    mergeMutationStatus,
    mergeResponse,
    handleClearStore,
  } = UseMergePersons()

  useEffect(() => {
    if (mergeMutationStatus.isSuccess) {
      showModalNotification({
        title: 'Records Merged Successfully',
        subtitle: 'The selected records have been merged into one',
        buttonText: 'View Merged Record',
        path: mergeResponse?.isVerified
          ? `${getRoutePath(DEFAULT_ROUTES.ACCOUNTS)}/${mergeResponse?.id}`
          : `${getRoutePath(DEFAULT_ROUTES.LEADS)}/${mergeResponse?.id}`,
      })
      handleClearStore()
    } else if (mergeMutationStatus.isError) {
      showModalNotificationError({
        title: 'Merge Failed',
        subtitle:
          'There was an issue merging the selected records. Please try again or check for any conflicts',
      })
    }
  }, [mergeMutationStatus.isSuccess, mergeMutationStatus.isError])

  return (
    <BasicTemplate
      isLoading={false}
      title="Person Merging Tool"
      breadCrumbItems={breadCrumbItems}
    >
      <Card shadow="lg" radius="sm" className={styles['default-card']}>
        <Text size="sm">
          To merge records, select a primary record that will be updated with
          the information chosen by the user.{' '}
          <Text span inherit fw={500} c="#E53E3E">
            Once you merge you cannot undo the process.
          </Text>
        </Text>
        <section>
          <Text mt={20} className={styles.title}>
            Associated Information
          </Text>
          <AssociatedInformation />
        </section>
        <section>
          <Text mt={20} className={styles.title}>
            Record Information
          </Text>
          {!!tableData.length ? (
            <RecordInformation
              tableData={tableData}
              recodrInformationForm={recodrInformationForm}
              setEntireColumn={setEntireColumn}
              isChecked={isChecked}
              setCellValue={setCellValue}
              onSubmit={onSubmit}
              isLoading={mergeMutationStatus.isLoading}
            />
          ) : (
            <LoadingSkeleton
              stackProps={{
                justify: 'space-between',
                h: '70vh',
              }}
              maxRange={10}
              height={'5%'}
              radius="sm"
            />
          )}
        </section>
      </Card>
    </BasicTemplate>
  )
}

export default withErrorBoundaryAndPolicyCheck(
  PersonMerginToolView,
  'is_get_person_by_id_enabled',
  true,
)
